import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExpenseCategories, useCreateExpense } from '@/hooks/useApi';
import type { ScannedExpenseData } from '@/hooks/useApi';
import { getScannedFile } from './ExpenseScanModal';
import toast from 'react-hot-toast';

const VAT_RATES = ['21', '12', '6', '0'];

export default function ExpenseCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: categories } = useExpenseCategories();
  const createExpense = useCreateExpense();

  const scannedData = (location.state as { scannedData?: ScannedExpenseData } | null)?.scannedData;

  const [date, setDate] = useState(scannedData?.date || new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState(scannedData?.description || '');
  const [categoryId, setCategoryId] = useState('');
  const [supplierName, setSupplierName] = useState(scannedData?.supplierName || '');
  const [amount, setAmount] = useState(scannedData?.netAmount || '');
  const [vatRate, setVatRate] = useState(scannedData?.vatRate || '21');
  const [vatDeductibility, setVatDeductibility] = useState('100');
  const [notes, setNotes] = useState('');
  const [receipt, setReceipt] = useState<File | null>(() => scannedData ? getScannedFile() : null);
  const [showScannedBanner, setShowScannedBanner] = useState(!!scannedData);

  // If we have total but no net, compute net from total and VAT rate
  useEffect(() => {
    if (scannedData && !scannedData.netAmount && scannedData.totalAmount) {
      const total = parseFloat(scannedData.totalAmount);
      const vat = parseFloat(scannedData.vatRate || '21');
      const net = total / (1 + vat / 100);
      setAmount(net.toFixed(2));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const netAmount = Number(amount || 0);
  const vatAmount = netAmount * (Number(vatRate) / 100);
  const totalAmount = netAmount + vatAmount;
  const deductibleVat = vatAmount * (Number(vatDeductibility) / 100);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const formData = new FormData();
    formData.append('date', date);
    formData.append('description', description);
    if (categoryId) formData.append('categoryId', categoryId);
    if (supplierName) formData.append('supplierName', supplierName);
    formData.append('amount', amount);
    formData.append('vatRate', vatRate);
    formData.append('vatDeductibility', vatDeductibility);
    if (notes) formData.append('notes', notes);
    if (receipt) formData.append('receipt', receipt);

    createExpense.mutate(
      {
        expenseDate: date,
        description,
        categoryId: categoryId || undefined,
        amount,
        vatRate,
        vatAmount: vatAmount.toFixed(2),
        deductiblePercentage: vatDeductibility,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          toast.success(t('expenses.created'));
          navigate('/expenses');
        },
      }
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">←</button>
        <h1 className="text-2xl font-bold">{t('expenses.addExpense')}</h1>
      </div>

      {showScannedBanner && (
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-200 p-4">
          <span className="text-2xl">📋</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">{t('expenses.scan.prefilled')}</p>
            <p className="text-xs text-blue-600 mt-0.5">{t('expenses.scan.prefilledHint')}</p>
          </div>
          <button type="button" className="text-blue-400 hover:text-blue-600" onClick={() => setShowScannedBanner(false)}>&times;</button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card card-body grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">{t('expenses.date')} *</label>
                <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('expenses.category')}</label>
                <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                  <option value="">{t('common.select')}</option>
                  {categories?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label">{t('expenses.description')} *</label>
                <input type="text" className="input" value={description} onChange={(e) => setDescription(e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('expenses.supplier')}</label>
                <input type="text" className="input" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
              </div>
            </div>

            <div className="card card-body grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">{t('expenses.netAmount')} *</label>
                <input type="number" step="0.01" className="input" value={amount} onChange={(e) => setAmount(e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('invoices.vat')}</label>
                <select className="input" value={vatRate} onChange={(e) => setVatRate(e.target.value)}>
                  {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
              <div>
                <label className="label">{t('expenses.vatDeductibility')}</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="100" className="input" value={vatDeductibility} onChange={(e) => setVatDeductibility(e.target.value)} />
                  <span className="text-gray-500">%</span>
                </div>
              </div>
            </div>

            <div className="card card-body">
              <label className="label">{t('expenses.receipt')}</label>
              {receipt && (
                <div className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded text-sm">
                  <span>📎</span>
                  <span className="flex-1 truncate">{receipt.name}</span>
                  <span className="text-gray-400">({(receipt.size / 1024).toFixed(0)} KB)</span>
                  <button type="button" className="text-red-400 hover:text-red-600" onClick={() => setReceipt(null)}>&times;</button>
                </div>
              )}
              <input
                type="file"
                accept="image/*,.pdf"
                className="input"
                onChange={(e) => setReceipt(e.target.files?.[0] || null)}
              />
            </div>

            <div className="card card-body">
              <label className="label">{t('common.notes')}</label>
              <textarea className="input min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="card card-body space-y-3 sidebar-sticky">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('expenses.netAmount')}</span>
                <span>€{netAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('invoices.vat')} ({vatRate}%)</span>
                <span>€{vatAmount.toFixed(2)}</span>
              </div>
              <hr />
              <div className="flex justify-between text-lg">
                <span className="font-bold">{t('invoices.total')}</span>
                <span className="font-bold">€{totalAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-600">
                <span>{t('expenses.deductibleVat')}</span>
                <span>€{deductibleVat.toFixed(2)}</span>
              </div>

              <button type="submit" className="btn btn-primary w-full mt-4" disabled={createExpense.isPending}>
                {createExpense.isPending ? t('common.saving') : t('expenses.addExpense')}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
